/**
 * Hook to run a show: given show script and current time, returns
 * active dialogue, active character, and motion sequence to play.
 * Designed so we can drive one or more puppets and a subtitle line from a single timeline.
 */

import { useMemo } from 'react'
import type { Show, Cue, ActionCue } from './types'
import type { MotionSequence } from '../llm/types'
import { getMotion } from './namedMotions'

export interface ShowStateAtTime {
  /** Current dialogue line to show (if any) */
  dialogue: { characterId: string; text: string } | null
  /** Character id that should be "active" (e.g. for single-puppet: who is speaking/acting) */
  activeCharacterId: string | null
  /** Motion sequence to play for the active character (from last action cue) */
  motion: MotionSequence | null
  /** Name of current action (e.g. "hit", "wave") for RL rest-length lookup */
  currentActionName: string | null
  /** Start time of the current motion (so we can sync control bar) */
  motionStartTime: number
  /** Next cue time (for progress / duration) */
  nextCueTime: number
}

function getCurrentDialogue(cues: Cue[], time: number): { characterId: string; text: string } | null {
  for (const c of cues) {
    if (c.startTime > time) break
    if (c.type === 'dialogue') {
      const end = c.startTime + (c.duration ?? 3)
      if (time < end) return { characterId: c.characterId, text: c.text }
    }
  }
  return null
}

function getCurrentActionAndStart(cues: Cue[], time: number): { characterId: string; action: string; startTime: number } | null {
  let last: ActionCue | null = null
  for (const c of cues) {
    if (c.startTime > time) break
    if (c.type === 'action') last = c
  }
  if (!last) return null
  const duration = last.duration ?? 1.5
  if (time >= last.startTime + duration) return null
  return { characterId: last.characterId, action: last.action, startTime: last.startTime }
}

function getNextCueTime(cues: Cue[], time: number): number {
  for (const c of cues) {
    if (c.startTime > time) return c.startTime
  }
  return time + 60
}

export function useShowRunner(show: Show | null, time: number): ShowStateAtTime {
  return useMemo((): ShowStateAtTime => {
    if (!show || !show.cues.length) {
      return {
        dialogue: null,
        activeCharacterId: null,
        motion: null,
        currentActionName: null,
        motionStartTime: 0,
        nextCueTime: time + 60,
      }
    }
    const dialogue = getCurrentDialogue(show.cues, time)
    const actionState = getCurrentActionAndStart(show.cues, time)
    const motion = actionState ? getMotion(actionState.action) : null
    const activeCharacterId =
      dialogue?.characterId ?? actionState?.characterId ?? (show.cues[0] && 'characterId' in show.cues[0] ? show.cues[0].characterId : null)
    return {
      dialogue: dialogue ?? null,
      activeCharacterId,
      motion: motion ?? null,
      currentActionName: actionState?.action ?? null,
      motionStartTime: actionState?.startTime ?? 0,
      nextCueTime: getNextCueTime(show.cues, time),
    }
  }, [show, time])
}
